import { Navigate, useLocation } from 'react-router-dom';

import { type AuthState, type User, useAuthStore } from '@/features/auth/model';

import { ENV } from '@/shared/config/env';
import { ROUTES } from '@/shared/config/routes';
import { logger } from '@/shared/lib/logger';

export const Permission = {
  AUTH: 'auth',
  ACCOUNT_NOT_DELETED: 'account_not_deleted',
  ADMIN: 'admin',
  SFW_ONLY: 'sfw_only', // Only accessible when NSFW is false
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

type PermissionChecker = (user: User | null) => boolean;

const permissionCheckers: Record<Permission, PermissionChecker> = {
  [Permission.AUTH]: (user) => !!user,
  [Permission.ACCOUNT_NOT_DELETED]: (user) => !!user && !user.isDeleted,
  [Permission.ADMIN]: (user) => !!user && user.isAdmin === true,
  [Permission.SFW_ONLY]: () => !ENV.NSFW, // Only accessible when NSFW is false
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  permissions?: Permission[];
}

export const ProtectedRoute = ({
  children,
  permissions = [Permission.AUTH],
}: ProtectedRouteProps) => {
  const user = useAuthStore((state: AuthState) => state.user);
  const isAuthenticated = useAuthStore((state: AuthState) => state.isAuthenticated);
  const isInitializing = useAuthStore((state: AuthState) => state.isInitializing);
  const location = useLocation();

  logger.auth.debug('ProtectedRoute check', {
    path: location.pathname,
    permissions,
    hasUser: !!user,
    isAuthenticated,
    isInitializing,
    userId: user?.id,
  });

  // Wait for auth initialization to complete before making routing decisions
  if (isInitializing) {
    logger.auth.debug('ProtectedRoute waiting for auth initialization');
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Check each permission
  for (const permission of permissions) {
    const checker = permissionCheckers[permission];

    // For AUTH permission, check both user and authenticated state
    if (permission === Permission.AUTH && (!checker(user) || !isAuthenticated)) {
      logger.auth.debug('ProtectedRoute AUTH permission failed', {
        permission,
        hasUser: !!user,
        isAuthenticated,
        path: location.pathname,
        redirectingTo: ROUTES.AUTH.REGISTER,
      });
      return <Navigate to={ROUTES.AUTH.REGISTER} state={{ from: location }} replace />;
    }

    if (!checker(user)) {
      // AUTH permission already handled above
      if (permission === Permission.AUTH) {
        logger.auth.debug('ProtectedRoute AUTH permission failed (secondary check)', {
          permission,
          hasUser: !!user,
          path: location.pathname,
          redirectingTo: ROUTES.AUTH.REGISTER,
        });
        return <Navigate to={ROUTES.AUTH.REGISTER} state={{ from: location }} replace />;
      }

      // Other permissions failed - redirect to home
      logger.auth.debug('ProtectedRoute permission failed', {
        permission,
        hasUser: !!user,
        path: location.pathname,
        redirectingTo: ROUTES.HOME,
      });
      return <Navigate to={ROUTES.HOME} replace />;
    }
  }

  logger.auth.debug('ProtectedRoute access granted', {
    path: location.pathname,
    permissions,
    userId: user?.id,
  });

  return <>{children}</>;
};
